/**
 * TabHeaderSelector Component
 *
 * Mobile-friendly campaign and route selector designed for tab headers.
 * Shows current campaign and active route, allowing quick switching.
 *
 * Design:
 * - Campaign name prominently displayed
 * - Active route shown below or beside campaign
 * - Touch-friendly dropdowns
 * - Remembers last active route per campaign
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, MapPin, Briefcase, Check } from 'lucide-react';
import { useCampaignStore } from '@features/campaigns/store/campaignStore';
import { useRouteStore } from '@features/routes/store/routeStore';
import { useIsMobile } from '@shared/hooks';
import { RouteHistorySubmenu } from './RouteHistorySubmenu';

export interface TabHeaderSelectorProps {
  /**
   * Whether to show route selector (default: true, available from all tabs)
   */
  showRouteSelector?: boolean;

  /**
   * Tab name for context
   */
  tabName?: string;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Compact mode for smaller headers
   */
  compact?: boolean;
}

/**
 * Tab Header Selector - Integrated campaign/route selector for tab headers
 */
export const TabHeaderSelector: React.FC<TabHeaderSelectorProps> = ({
  showRouteSelector = true,
  tabName = '',
  className = '',
  compact = false,
}) => {
  const isMobile = useIsMobile();
  const { campaigns, selectedCampaignId, setSelectedCampaign } = useCampaignStore();
  const {
    routeHistory,
    activeRoutesByCampaign,
    setActiveRoute,
    fetchRouteHistory,
    fetchActiveRoute,
    loadRoute,
  } = useRouteStore();

  const [showCampaignMenu, setShowCampaignMenu] = useState(false);
  const [showRouteMenu, setShowRouteMenu] = useState(false);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false);
  // Track which campaign has expanded route history submenu (default: selected campaign)
  const [expandedCampaignId, setExpandedCampaignId] = useState<string | null>(selectedCampaignId);

  const campaignMenuRef = useRef<HTMLDivElement>(null);
  const routeMenuRef = useRef<HTMLDivElement>(null);

  // Get selected campaign
  const selectedCampaign = campaigns?.find(c => c.campaign_id === selectedCampaignId);

  // Get active route for current campaign
  const activeRouteId = selectedCampaignId
    ? activeRoutesByCampaign[selectedCampaignId]
    : null;

  // Safely handle routeHistory (ensure it's an array)
  const routeHistoryArray = Array.isArray(routeHistory) ? routeHistory : [];
  const activeRoute = routeHistoryArray.find(r => r.route_id === activeRouteId);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (campaignMenuRef.current && !campaignMenuRef.current.contains(event.target as Node)) {
        setShowCampaignMenu(false);
      }
      if (routeMenuRef.current && !routeMenuRef.current.contains(event.target as Node)) {
        setShowRouteMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch routes when campaign changes or when submenu is expanded
  useEffect(() => {
    const campaignToFetch = expandedCampaignId || selectedCampaignId;
    console.log('[TabHeaderSelector] useEffect triggered:', {
      selectedCampaignId,
      expandedCampaignId,
      campaignToFetch,
      showRouteSelector,
    });
    if (campaignToFetch && showRouteSelector) {
      console.log('[TabHeaderSelector] Fetching route history and active route for:', campaignToFetch);
      setIsLoadingRoutes(true);
      Promise.all([
        fetchRouteHistory(campaignToFetch),
        fetchActiveRoute(campaignToFetch),
      ]).then(() => {
        console.log('[TabHeaderSelector] Fetch completed for:', campaignToFetch);
      }).finally(() => {
        setIsLoadingRoutes(false);
      });
    }
  }, [selectedCampaignId, expandedCampaignId, showRouteSelector, fetchRouteHistory, fetchActiveRoute]);

  // Auto-expand selected campaign's submenu when dropdown opens
  useEffect(() => {
    if (showCampaignMenu && selectedCampaignId) {
      setExpandedCampaignId(selectedCampaignId);
    }
  }, [showCampaignMenu, selectedCampaignId]);

  const handleCampaignSelect = (campaignId: string) => {
    setSelectedCampaign(campaignId);
    setExpandedCampaignId(campaignId);
    // Don't close menu - let user select a route from submenu
    // setShowCampaignMenu(false);
  };

  const handleRouteSelect = async (routeId: string) => {
    if (selectedCampaignId) {
      await setActiveRoute(selectedCampaignId, routeId);
      // Auto-load the route for viewing
      await loadRoute(routeId);
      setShowRouteMenu(false);
    }
  };

  // Handle route selection from within campaign dropdown submenu
  const handleSubmenuRouteSelect = async (routeId: string) => {
    const campaignId = expandedCampaignId || selectedCampaignId;
    if (campaignId) {
      // First select the campaign if different
      if (campaignId !== selectedCampaignId) {
        setSelectedCampaign(campaignId);
      }
      // Set as active route and persist
      await setActiveRoute(campaignId, routeId);
      // Auto-load the route for viewing
      await loadRoute(routeId);
      // Close the dropdown
      setShowCampaignMenu(false);
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'planning':
        return 'bg-blue-500/20 text-blue-400 border-brand-500/50';
      case 'completed':
        return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50';
      default:
        return 'bg-gray-500/20 text-dark-muted border-gray-500/50';
    }
  };

  if (compact) {
    // Compact mode - single line with campaign and route
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {/* Campaign Selector */}
        <div className="relative" ref={campaignMenuRef}>
          <button
            onClick={() => setShowCampaignMenu(!showCampaignMenu)}
            className="
              flex items-center gap-1.5
              px-2 py-1
              bg-dark-surface/50 hover:bg-dark-surface
              border border-dark-border/30 hover:border-brand-600/50
              rounded
              text-xs
              transition-all
              max-w-[140px]
            "
          >
            <Briefcase className="w-3 h-3 text-brand-500 flex-shrink-0" />
            <span className="truncate">
              {selectedCampaign?.campaign_name || 'Select'}
            </span>
            <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${showCampaignMenu ? 'rotate-180' : ''}`} />
          </button>

          {/* Campaign Dropdown with Route History Submenu */}
          {showCampaignMenu && (
            <div className="
              absolute top-full mt-1 left-0
              min-w-[280px]
              bg-dark-surface
              border border-dark-border/30
              rounded-lg
              shadow-xl
              z-50
              max-h-[400px]
              overflow-y-auto
            ">
              {campaigns.map(camp => {
                const isSelected = selectedCampaignId === camp.campaign_id;
                const isExpanded = expandedCampaignId === camp.campaign_id;
                const campActiveRouteId = activeRoutesByCampaign[camp.campaign_id] || null;

                return (
                  <div key={camp.campaign_id}>
                    <button
                      onClick={() => handleCampaignSelect(camp.campaign_id)}
                      className={`
                        w-full px-3 py-2
                        flex items-center justify-between gap-2
                        hover:bg-dark-surface/50
                        text-left
                        text-xs
                        ${isSelected ? 'bg-dark-surface/50' : ''}
                      `}
                    >
                      <span className="truncate">{camp.campaign_name}</span>
                      <div className="flex items-center gap-1">
                        {isSelected && (
                          <Check className="w-3 h-3 text-brand-500 flex-shrink-0" />
                        )}
                      </div>
                    </button>

                    {/* Route History Submenu - shown for expanded campaign */}
                    {showRouteSelector && isExpanded && (
                      <RouteHistorySubmenu
                        routeHistory={routeHistoryArray.filter(r => r.campaign_id === camp.campaign_id)}
                        activeRouteId={campActiveRouteId}
                        isLoading={isLoadingRoutes}
                        onSelectRoute={handleSubmenuRouteSelect}
                        limit={5}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Route Selector */}
        {showRouteSelector && selectedCampaignId && (
          <div className="relative" ref={routeMenuRef}>
            <button
              onClick={() => setShowRouteMenu(!showRouteMenu)}
              className="
                flex items-center gap-1.5
                px-2 py-1
                bg-dark-surface/50 hover:bg-dark-surface
                border border-dark-border/30 hover:border-brand-600/50
                rounded
                text-xs
                transition-all
                max-w-[140px]
              "
              disabled={isLoadingRoutes}
            >
              <MapPin className="w-3 h-3 text-brand-500 flex-shrink-0" />
              <span className="truncate">
                {isLoadingRoutes ? 'Loading...' : activeRoute?.route_name || 'Select Route'}
              </span>
              <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${showRouteMenu ? 'rotate-180' : ''}`} />
            </button>

            {/* Route Dropdown */}
            {showRouteMenu && !isLoadingRoutes && (
              <div className="
                absolute top-full mt-1 left-0
                min-w-[250px]
                bg-dark-surface
                border border-dark-border/30
                rounded-lg
                shadow-xl
                z-50
                max-h-[300px]
                overflow-y-auto
              ">
                {routeHistory.length === 0 ? (
                  <div className="px-3 py-4 text-center text-dark-muted text-xs">
                    No routes available
                  </div>
                ) : (
                  routeHistory.map(route => (
                    <button
                      key={route.route_id}
                      onClick={() => handleRouteSelect(route.route_id)}
                      className="
                        w-full px-3 py-2
                        flex items-center justify-between gap-2
                        hover:bg-dark-surface/50
                        text-left
                      "
                    >
                      <div className="min-w-0">
                        <div className="text-xs truncate">{route.route_name}</div>
                        <div className="text-[10px] text-dark-muted">
                          {route.total_stops} stops • {route.total_weeks} weeks
                        </div>
                      </div>
                      {activeRouteId === route.route_id && (
                        <Check className="w-3 h-3 text-brand-500 flex-shrink-0" />
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full mode - stacked layout for mobile tabs
  return (
    <div className={`space-y-2 ${className}`}>
      {/* Campaign Section */}
      <div className="relative" ref={campaignMenuRef}>
        <button
          onClick={() => setShowCampaignMenu(!showCampaignMenu)}
          className="
            w-full
            flex items-center justify-between
            px-3 py-2
            bg-dark-surface/80 hover:bg-dark-surface
            border border-dark-border/30 hover:border-brand-600/50
            rounded-lg
            transition-all
            group
          "
        >
          <div className="flex items-center gap-2 min-w-0">
            <Briefcase className="w-4 h-4 text-brand-500 flex-shrink-0" />
            <div className="text-left min-w-0">
              <div className="text-xs text-dark-muted">Campaign</div>
              <div className="text-sm font-medium text-dark-text truncate">
                {selectedCampaign?.campaign_name || 'Select Campaign'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedCampaign && (
              <span className={`
                px-2 py-0.5
                text-[10px] font-medium
                rounded border
                ${getStatusBadgeColor(selectedCampaign.status)}
              `}>
                {selectedCampaign.status.toUpperCase()}
              </span>
            )}
            <ChevronDown className={`
              w-4 h-4 text-dark-muted
              transition-transform
              ${showCampaignMenu ? 'rotate-180' : ''}
            `} />
          </div>
        </button>

        {/* Campaign Dropdown with Route History Submenu */}
        {showCampaignMenu && (
          <div className="
            absolute top-full mt-2 left-0 right-0
            bg-dark-surface
            border border-dark-border/30
            rounded-lg
            shadow-xl
            z-50
            max-h-[60vh]
            overflow-y-auto
          ">
            <div className="py-2">
              {campaigns.length === 0 ? (
                <div className="px-4 py-8 text-center text-dark-muted text-sm">
                  No campaigns available
                </div>
              ) : (
                campaigns.map(camp => {
                  const isSelected = selectedCampaignId === camp.campaign_id;
                  const isExpanded = expandedCampaignId === camp.campaign_id;
                  const campActiveRouteId = activeRoutesByCampaign[camp.campaign_id] || null;

                  return (
                    <div key={camp.campaign_id}>
                      <button
                        onClick={() => handleCampaignSelect(camp.campaign_id)}
                        className={`
                          w-full
                          px-4 py-3
                          flex items-center justify-between gap-3
                          hover:bg-dark-surface/50
                          active:bg-dark-surface/60
                          transition-all duration-150
                          text-left
                          ${isSelected ? 'bg-dark-surface/50' : ''}
                        `}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Briefcase className="w-4 h-4 text-dark-muted flex-shrink-0" />
                          <span className="text-sm text-dark-text truncate">
                            {camp.campaign_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`
                            px-2 py-0.5
                            text-[10px] font-medium
                            rounded border
                            ${getStatusBadgeColor(camp.status)}
                          `}>
                            {camp.status.toUpperCase()}
                          </span>
                          {isSelected && (
                            <Check className="w-4 h-4 text-brand-500 flex-shrink-0" />
                          )}
                        </div>
                      </button>

                      {/* Route History Submenu - shown for expanded campaign */}
                      {showRouteSelector && isExpanded && (
                        <div className="mx-3 mb-2">
                          <RouteHistorySubmenu
                            routeHistory={routeHistoryArray.filter(r => r.campaign_id === camp.campaign_id)}
                            activeRouteId={campActiveRouteId}
                            isLoading={isLoadingRoutes}
                            onSelectRoute={handleSubmenuRouteSelect}
                            limit={5}
                          />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Route Section */}
      {showRouteSelector && selectedCampaignId && (
        <div className="relative" ref={routeMenuRef}>
          <button
            onClick={() => setShowRouteMenu(!showRouteMenu)}
            className="
              w-full
              flex items-center justify-between
              px-3 py-2
              bg-dark-surface/80 hover:bg-dark-surface
              border border-dark-border/30 hover:border-brand-600/50
              rounded-lg
              transition-all
              group
            "
            disabled={isLoadingRoutes}
          >
            <div className="flex items-center gap-2 min-w-0">
              <MapPin className="w-4 h-4 text-brand-500 flex-shrink-0" />
              <div className="text-left min-w-0">
                <div className="text-xs text-dark-muted">Active Route</div>
                <div className="text-sm font-medium text-dark-text truncate">
                  {isLoadingRoutes
                    ? 'Loading routes...'
                    : activeRoute?.route_name || 'Select Route'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {activeRoute && !isLoadingRoutes && (
                <span className="text-xs text-dark-muted">
                  {activeRoute.total_stops} stops
                </span>
              )}
              <ChevronDown className={`
                w-4 h-4 text-dark-muted
                transition-transform
                ${showRouteMenu ? 'rotate-180' : ''}
              `} />
            </div>
          </button>

          {/* Route Dropdown */}
          {showRouteMenu && !isLoadingRoutes && (
            <div className="
              absolute top-full mt-2 left-0 right-0
              bg-dark-surface
              border border-dark-border/30
              rounded-lg
              shadow-xl
              z-50
              max-h-[60vh]
              overflow-y-auto
            ">
              <div className="py-2">
                {routeHistoryArray.length === 0 ? (
                  <div className="px-4 py-8 text-center text-dark-muted text-sm">
                    No routes available for this campaign
                  </div>
                ) : (
                  routeHistoryArray.map(route => (
                    <button
                      key={route.route_id}
                      onClick={() => handleRouteSelect(route.route_id)}
                      className="
                        w-full
                        px-4 py-3
                        flex items-center justify-between gap-3
                        hover:bg-dark-surface/50
                        active:bg-dark-surface/60
                        transition-all duration-150
                        text-left
                      "
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <MapPin className="w-4 h-4 text-dark-muted flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm text-dark-text truncate">
                            {route.route_name}
                          </div>
                          <div className="text-xs text-dark-muted">
                            {route.total_stops} stops • {route.total_weeks} weeks
                          </div>
                        </div>
                      </div>
                      {activeRouteId === route.route_id && (
                        <Check className="w-4 h-4 text-brand-500 flex-shrink-0" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TabHeaderSelector;